import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  CreditCard,
  Banknote,
  FileText,
  Users,
  Calculator,
  Percent,
  Printer,
  Sparkles,
  ChefHat,
  CheckCircle2,
  Clock,
  ThumbsUp,
  ArrowRight,
  Filter,
  Calendar,
  ChevronRight,
  ArrowLeft,
  FileSpreadsheet,
  Moon,
  Wallet,
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { toast } from '../components/ui/notify';
import { Helmet } from "react-helmet";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

import { base_url } from "../api/index";
import { usePermissions } from "../context/PermissionsContext";
import AccessDenied from "../components/AccessDenied";
import PasswordScreen from "../components/ScreenPassword";
import { PosLoader } from "../components/pos/PosLoader";
import { useI18n, tStatic } from "../i18n/I18nContext";
import { useConfirm } from "../components/ConfirmDialog";

const getHeaders = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

const bankNotes = [5, 10, 20, 50, 100, 200];

const paymentMethods = [
  {
    id: "cash",
    type: "cash",
    icon: Banknote,
    labelKey: "kasa.method.cash",
    color: "text-green-600",
    gradient: "from-green-500 to-emerald-600",
  },
  {
    id: "bank",
    type: "card",
    icon: CreditCard,
    labelKey: "kasa.method.bankManual",
    color: "text-indigo-400",
    gradient: "from-indigo-400 to-blue-400",
  },
  {
    id: "bank_pos",
    type: "card",
    icon: CreditCard,
    labelKey: "kasa.method.posTerminal",
    color: "text-blue-600",
    gradient: "from-blue-500 to-indigo-600",
  },
  {
    id: "bank_online",
    type: "card",
    icon: Sparkles,
    labelKey: "kasa.method.onlineLink",
    color: "text-purple-600",
    gradient: "from-purple-500 to-pink-600",
  },
  {
    id: "customer_balance",
    type: "cari",
    icon: FileText,
    labelKey: "kasa.method.credit",
    color: "text-orange-600",
    gradient: "from-orange-500 to-red-500",
  },
];

const normalizePaymentType = (
  type: any,
): "cash" | "bank" | "customer_balance" => {
  const value = String(type || "").toLowerCase();
  if (value.includes("cash") || value.includes("nakit")) return "cash";
  if (
    value.includes("customer") ||
    value.includes("balance") ||
    value.includes("cari")
  )
    return "customer_balance";
  return "bank";
};

const formatPaymentDate = (value: any) => {
  if (!value) return { date: "-", time: "-" };
  const parsed = new Date(value);
  if (!Number.isFinite(parsed.getTime()))
    return { date: String(value), time: "-" };
  return {
    date: parsed.toLocaleDateString("tr-TR"),
    time: parsed.toLocaleTimeString("tr-TR", {
      hour: "2-digit",
      minute: "2-digit",
    }),
  };
};

const normalizeReportPayments = (payments: any[] = []) =>
  payments.map((payment, index) => {
    const sourceDate =
      payment.date ||
      payment.created_at ||
      payment.paid_at ||
      payment.close_date;
    const { date, time } = formatPaymentDate(sourceDate);
    const orderName =
      payment.order_name ||
      payment.table_name ||
      payment.table?.name ||
      (payment.order_id ? tStatic("kasa.orderNo", { id: payment.order_id }) : "-");
    return {
      ...payment,
      id: payment.id ?? payment.payment_id ?? index + 1,
      transactionNo:
        payment.transaction_id ||
        payment.reference ||
        `TRX-${String(payment.id ?? index + 1).padStart(3, "0")}`,
      order_name: orderName,
      user_name:
        payment.user_name ||
        payment.cashier ||
        payment.user?.name ||
        payment.staff?.name ||
        "-",
      amount: Number(payment.amount ?? payment.total_amount ?? 0),
      total_amount: Number(payment.total_amount ?? payment.amount ?? 0),
      type: normalizePaymentType(
        payment.type || payment.payment_type || payment.method,
      ),
      date,
      time,
      raw_date: sourceDate,
    };
  });

export default function GunlukKasa() {
  const { t } = useI18n();
  const { confirm, confirmDialog } = useConfirm();
  const [activeTab, setActiveTab] = useState("checkout"); // 'checkout' | 'reports'
  const [selectedTable, setSelectedTable] = useState(null);
  const [occupiedTables, setOccupiedTables] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  // Checkout State
  const [orderDetails, setOrderDetails] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState("");
  const [splitMode, setSplitMode] = useState("none"); // 'none' | 'equal' | 'items'
  const [discount, setDiscount] = useState(0);
  const [cashGiven, setCashGiven] = useState("");
  const [selectedItems, setSelectedItems] = useState<any[]>([]); // Array of pivot IDs
  const [customers, setCustomers] = useState<any[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const { hasPermission } = usePermissions();

  // Reports State
  const [reportData, setReportData] = useState<any[]>([]);
  const [reportTotals, setReportTotals] = useState<any>({});
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [paymentTypeFilter, setPaymentTypeFilter] = useState("all");

  useEffect(() => {
    fetchOccupiedTables();
    fetchCustomers();
    if (activeTab === "reports") {
      fetchReports();
    }
    const interval = setInterval(() => {
      if (activeTab === "checkout" && !selectedTable) {
        fetchOccupiedTables();
      }
    }, 15000);
    return () => clearInterval(interval);
  }, [activeTab]);

  const fetchOccupiedTables = async () => {
    try {
      const response = await axios.get(`${base_url}/tables`, getHeaders());
      const allTables = response.data.tables || [];
      setOccupiedTables(allTables.filter((t) => !t.is_available));
    } catch (err: any) {
      handleApiError(err);
    }
  };

  const fetchCustomers = async () => {
    // `/customers` cloud endpoint-i `manage-customers` icazəsi tələb edir — icazə
    // yoxdursa çağırma (403 spam-ının qarşısı). 401/403 sakitcə udulur.
    if (!hasPermission("manage-customers")) return;
    try {
      const res = await axios.get(`${base_url}/customers`, getHeaders());
      setCustomers(res.data);
    } catch (err: any) {
      const s = err?.response?.status;
      if (s !== 401 && s !== 403) console.error("Error fetching customers:", err);
    }
  };

  const fetchOrderDetails = async (tableId) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${base_url}/tables/${tableId}/order`,
        getHeaders(),
      );
      const table = response.data.table;
      const orders = table.orders;

      const items = orders.flatMap((order) =>
        order.stocks.map((stock) => ({
          id: stock.pivot?.id || stock.id,
          pivot_id: stock.pivot?.id,
          name: stock.name,
          quantity: stock.pivot?.quantity || stock.quantity,
          price: stock.price,
          status: stock.pivot?.status || "served",
          notes: stock.pivot?.notes ? [stock.pivot.notes] : [],
        })),
      );

      setOrderDetails({
        id: orders[0]?.order_id,
        tableId: table.id,
        tableName: table.name,
        guests: table.guest_count || 1,
        waiter: table.waiter_name || t("pos.waiter"),
        items,
        total: orders[0]?.total_price || 0,
        prepayment: orders[0]?.total_prepayment || 0,
      });
    } catch (err: any) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchReports = async (override?: { from?: string; to?: string }) => {
    setLoading(true);
    // setState asinxrondur — gün sonundan sonra tarixi dərhal işlətmək üçün override.
    const from = override?.from ?? startDate;
    const to = override?.to ?? endDate;
    const params = new URLSearchParams();
    if (from) params.append("from", from);
    if (to) params.append("to", to);

    try {
      const response = await axios.get(
        `${base_url}/report?${params.toString()}`,
        getHeaders(),
      );
      const payments = normalizeReportPayments(response.data?.payments || []);
      setReportData(payments);
      setReportTotals({
        totalKasa: Number(
          response.data?.grand_total ?? response.data?.total_amount ?? 0,
        ),
        totalCash: Number(response.data?.total_cash ?? 0),
        totalBank: Number(
          response.data?.total_bank ?? response.data?.total_card ?? 0,
        ),
        totalCustomerBalance: Number(
          response.data?.total_customer_balance ?? 0,
        ),
        paymentCount: Number(response.data?.payment_count ?? payments.length),
      });
    } catch (err: any) {
      try {
        const fallbackParams = new URLSearchParams();
        if (from) fallbackParams.append("open_date", from);
        if (to) fallbackParams.append("close_date", to);
        const fallback = await axios.get(
          `${base_url}/payments?${fallbackParams.toString()}`,
          getHeaders(),
        );
        const payments = normalizeReportPayments(fallback.data?.payments || []);
        setReportData(payments);
        setReportTotals({
          totalKasa: Number(
            fallback.data?.total_amount ??
            payments.reduce((sum, p) => sum + p.total_amount, 0),
          ),
          totalCash: Number(
            fallback.data?.total_cash ??
            payments
              .filter((p) => p.type === "cash")
              .reduce((sum, p) => sum + p.total_amount, 0),
          ),
          totalBank: Number(
            fallback.data?.total_bank ??
            payments
              .filter((p) => p.type === "bank")
              .reduce((sum, p) => sum + p.total_amount, 0),
          ),
          totalCustomerBalance: Number(
            fallback.data?.total_customer_balance ??
            payments
              .filter((p) => p.type === "customer_balance")
              .reduce((sum, p) => sum + p.total_amount, 0),
          ),
          paymentCount: payments.length,
        });
      } catch (fallbackErr: any) {
        handleApiError(fallbackErr);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApiError = (err) => {
    if (err.response?.status === 403) {
      setAccessDenied(true);
    } else {
      console.error("API Error:", err);
      toast.error(t("kasa.connError"));
    }
  };

  const handleTableSelect = (table) => {
    setSelectedTable(table);
    fetchOrderDetails(table.id);
    // Reset checkout state
    setSelectedPayment("");
    setSplitMode("none");
    setDiscount(0);
    setCashGiven("");
    setSelectedItems([]);
  };

  const handleBackToTables = () => {
    setSelectedTable(null);
    setOrderDetails(null);
    fetchOccupiedTables();
  };

  // Calculations
  const subtotal = orderDetails?.total || 0;
  const discountAmount = (subtotal * discount) / 100;
  const remainingAfterPrepayment = subtotal - (orderDetails?.prepayment || 0);
  const totalAfterDiscount = Math.max(
    0,
    remainingAfterPrepayment - discountAmount,
  );

  const amountToPay =
    splitMode === "items"
      ? orderDetails?.items
        .filter((item) => selectedItems.includes(item.id))
        .reduce((sum, item) => sum + item.price * item.quantity, 0)
      : splitMode === "equal"
        ? totalAfterDiscount / orderDetails?.guests
        : totalAfterDiscount;

  const change = cashGiven
    ? Math.max(0, parseFloat(cashGiven) - amountToPay)
    : 0;

  const handlePayment = async () => {
    if (!selectedPayment) {
      toast.warning(t("kasa.selectMethod"));
      return;
    }

    if (selectedPayment === "customer_balance" && !selectedCustomerId) {
      toast.warning(t("kasa.selectCreditCustomer"));
      return;
    }

    const payload = {
      discount: parseFloat(String(discount)) || 0,
      received_amount: parseFloat(String(cashGiven)) || 0,
      remaining_amount: parseFloat(String(change.toFixed(2))),
      total_amount: parseFloat(String(subtotal.toFixed(2))),
      discounted_total: parseFloat(String(totalAfterDiscount.toFixed(2))),
      prepaid_amount: parseFloat((orderDetails?.prepayment || 0).toFixed(2)),
      shares: [],
    };

    if (splitMode === "equal") {
      // Logic for sharing equally might need back-end support or we send separate shares
      // For now, following original logic where parca-ode sends multiple shares
      for (let i = 0; i < (orderDetails?.guests || 1); i++) {
        payload.shares.push({
          type:
            selectedPayment === "cash"
              ? "cash"
              : selectedPayment.startsWith("bank")
                ? "bank"
                : "customer_balance",
          amount: parseFloat(amountToPay.toFixed(2)),
          customer_id: selectedCustomerId || null,
          items: orderDetails.items.map((item) => ({
            name: item.name,
            quantity: 1 / (orderDetails.guests || 1),
            price: item.price,
          })),
        });
      }
    } else {
      payload.shares.push({
        type:
          selectedPayment === "cash"
            ? "cash"
            : selectedPayment.startsWith("bank")
              ? "bank"
              : "customer_balance",
        amount: parseFloat(amountToPay.toFixed(2)),
        customer_id: selectedCustomerId || null,
        items:
          splitMode === "items"
            ? orderDetails.items
              .filter((i) => selectedItems.includes(i.id))
              .map((i) => ({
                name: i.name,
                quantity: i.quantity,
                price: i.price,
              }))
            : orderDetails.items.map((i) => ({
              name: i.name,
              quantity: i.quantity,
              price: i.price,
            })),
      });
    }

    try {
      setLoading(true);

      // Handle special bank payment integrations
      if (selectedPayment === "bank_pos") {
        const res = await axios.post(
          `${base_url}/payments/pos/sale`,
          {
            order_id: Number(orderDetails.id),
            amount: parseFloat(amountToPay.toFixed(2)),
          },
          getHeaders(),
        );

        // POS islem basariliysa, adisyonu da kapatmak icin asagiya devam et
        toast.info(t("kasa.posApproved"));
      } else if (selectedPayment === "bank_online") {
        const response = await axios.post(
          `${base_url}/payments/online/create`,
          {
            order_id: Number(orderDetails.id),
            amount: parseFloat(amountToPay.toFixed(2)),
          },
          getHeaders(),
        );

        if (response.data && response.data.redirect_url) {
          window.open(response.data.redirect_url, "_blank");
          toast.info(t("kasa.onlineOpened"));
        } else {
          toast.success(t("kasa.onlineCreated"));
        }
        handleBackToTables();
        return; // Skip normal payment payload as Online Callback does it
      }

      // Record the payment in our system for cash, bank_pos (after pos/sale), or customer_balance
      await axios.post(
        `${base_url}/orders/${orderDetails.id}/payments`,
        payload,
        getHeaders(),
      );
      toast.success(t("kasa.paySuccess"));
      handleBackToTables();
    } catch (err: any) {
      console.error("Payment error:", err);
      toast.error(
        err.response?.data?.message || t("kasa.payError"),
      );
    } finally {
      setLoading(false);
    }
  };

  const toggleItemSelection = (id) => {
    if (splitMode !== "items") return;
    if (selectedItems.includes(id)) {
      setSelectedItems(selectedItems.filter((i) => i !== id));
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };

  const handleQuickChange = (note) => {
    const current = parseFloat(cashGiven) || 0;
    setCashGiven((current + note).toString());
  };

  const filteredReportData = reportData.filter((item) => {
    if (paymentTypeFilter === "all") return true;
    if (paymentTypeFilter === "bank") return item.type === "bank";
    return item.type === paymentTypeFilter;
  });

  // Report Actions
  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(
      filteredReportData.map((d) => ({
        IslemNo: d.transactionNo,
        Masa: d.order_name,
        Tarih: d.date,
        Saat: d.time,
        Toplam: d.total_amount,
        OdemeTipi: d.type,
        Personel: d.user_name,
      })),
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t("kasa.excelSheet"));
    XLSX.writeFile(wb, "kasa_raporu.xlsx");
  };

  const handleEndOfDay = async () => {
    if (!(await confirm({ message: t("kasa.eodConfirm"), variant: "danger" }))) return;
    try {
      setLoading(true);
      await axios.post(`${base_url}/payments/end-of-day`, {}, getHeaders());
      toast.success(t("kasa.eodSuccess"));
      // Növbə bağlandıqdan sonra "cari açıq növbə" qalmır → filtrsiz hesabat BOŞ olur
      // və hər şey silinmiş kimi görünür. Tarix filtrini bu günə qoyub yenidən çəkirik
      // ki, bağlanmış növbənin rəqəmləri ekranda qalsın.
      const today = new Date().toISOString().slice(0, 10);
      setStartDate(today);
      setEndDate(today);
      await fetchReports({ from: today, to: today });
    } catch (err: any) {
      console.error(err);
      toast.error(
        err.response?.data?.message || t("kasa.eodError"),
      );
    } finally {
      setLoading(false);
    }
  };

  if (accessDenied)
    return <AccessDenied onClose={() => setAccessDenied(false)} />;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#12141c] dark:border-[#2a2e3a]/50 dark:bg-[#0f1117] flex flex-col pt-4">
      <Helmet>
        <title>{activeTab === "checkout" ? t("kasa.titleCheckout") : t("kasa.titleReports")} | Smartcafe</title>
      </Helmet>
      {/* Toast: App-dəki tək qlobal ToastContainer istifadə olunur. */}
      <PasswordScreen />

      {/* Header & Tabs */}
      <div className="bg-white dark:bg-[#1a1d27] border-b border-gray-100 dark:border-[#2a2e3a] p-6 mx-4 rounded-t-2xl shadow-sm mb-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-6">
          <div>
            <h1 className="text-3xl font-bold mb-1 text-slate-800 dark:text-white">
              {activeTab === "checkout" ? t("kasa.titleCheckout") : t("kasa.titleReports")}
            </h1>
            <p className="text-slate-500 dark:text-slate-400 font-medium">
              {activeTab === "checkout"
                ? t("kasa.subCheckout")
                : t("kasa.subReports")}
            </p>
          </div>

          <div className="flex gap-1 bg-slate-100 dark:bg-[#141824] rounded-xl p-1">
            <button
              onClick={() => setActiveTab("checkout")}
              className={`px-6 py-2.5 rounded-lg font-bold text-sm transition-all flex items-center gap-2 ${activeTab === "checkout" ? "bg-white dark:bg-slate-700 text-[var(--primary)] shadow-sm" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-white"}`}
            >
              <CreditCard className="w-4 h-4" />
              {t("kasa.tab.active")}
            </button>
            <button
              onClick={() => {
                setActiveTab("reports");
                fetchReports();
              }}
              className={`px-6 py-2.5 rounded-lg font-bold text-sm transition-all flex items-center gap-2 ${activeTab === "reports" ? "bg-white dark:bg-slate-700 text-[var(--primary)] shadow-sm" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-white"}`}
            >
              <FileText className="w-4 h-4" />
              {t("kasa.tab.history")}
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 px-4 overflow-hidden">
        <AnimatePresence mode="wait">
          {activeTab === "checkout" ? (
            <motion.div
              key="checkout-tab"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              className="h-full flex flex-col"
            >
              {!selectedTable ? (
                // Table Selection Mode
                <div className="flex-1 overflow-y-auto pb-10">
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
                    {occupiedTables.length > 0 ? (
                      occupiedTables.map((table) => (
                        <motion.button
                          key={table.id}
                          whileHover={{ scale: 1.03, y: -5 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleTableSelect(table)}
                          className="bg-white dark:bg-[#1a1d27] dark:text-gray-100 dark:border-[#2a2e3a] p-6 rounded-3xl border border-slate-100 shadow-sm hover:shadow-xl hover:border-[var(--primary)]/40 dark:hover:border-[var(--primary)]/50 dark:shadow-none transition-all text-center relative overflow-hidden group"
                        >
                          <div className="absolute top-0 right-0 w-20 h-20 bg-[var(--primary)] rounded-bl-[3rem] opacity-90">
                          </div>
                          <div className="w-16 h-16 bg-[var(--primary)]/10 text-[var(--primary)] rounded-2xl flex items-center justify-center mx-auto mb-4 font-black text-2xl transition-all">
                            {table.name?.replace(/[^0-9]/g, "")}
                          </div>
                          <h3 className="font-bold text-slate-800 dark:text-gray-100 mb-1">
                            {table.name}
                          </h3>
                          <p className="text-xl font-black text-[var(--primary)]">
                            ₼ {Number(table.total_price).toFixed(2)}
                          </p>
                          <div className="mt-3 flex items-center justify-center gap-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                            <Users className="w-3 h-3" />
                            {t("kasa.people", { n: table.guest_count || 1 })}
                          </div>
                        </motion.button>
                      ))
                    ) : (
                      <div className="col-span-full py-20 text-center">
                        <div className="w-24 h-24 bg-slate-100 dark:bg-[#141824] dark:border-[#2a2e3a] rounded-full flex items-center justify-center mx-auto mb-4">
                          <CheckCircle2 className="w-12 h-12 text-slate-300" />
                        </div>
                        <h2 className="text-xl font-bold text-slate-800">
                          {t("kasa.allPaid")}
                        </h2>
                        <p className="text-slate-500">
                          {t("kasa.noPending")}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                // Checkout Detail Mode
                <div className="h-full flex flex-col lg:flex-row gap-6 pb-6 overflow-hidden">
                  {/* Left Side: Order Items */}
                  <div className="flex-1 flex flex-col gap-6 min-h-0">
                    {/* Table Info Header */}
                    <div className="bg-white dark:bg-[#1a1d27] dark:text-gray-100 dark:border-[#2a2e3a] rounded-3xl p-5 border border-slate-100 shadow-sm flex items-center justify-between shrink-0">
                      <div className="flex items-center gap-4">
                        <button
                          onClick={handleBackToTables}
                          className="w-10 h-10 rounded-xl bg-slate-50 dark:bg-[#12141c] dark:border-[#2a2e3a] hover:bg-slate-100 dark:bg-[#141824] dark:border-[#2a2e3a] flex items-center justify-center text-slate-400 hover:text-slate-600 transition-all"
                        >
                          <ChevronRight className="w-5 h-5 rotate-180" />
                        </button>
                        <div>
                          <h2 className="text-xl font-bold text-slate-800">
                            {t("common.table")} {orderDetails?.tableName}
                          </h2>
                          <div className="flex items-center gap-3 text-xs font-semibold text-slate-400 mt-0.5">
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />{" "}
                              {t("kasa.people", { n: orderDetails?.guests })}
                            </span>
                            <span>•</span>
                            <span className="flex items-center gap-1">
                              <ChefHat className="w-3 h-3" />{" "}
                              {orderDetails?.waiter}
                            </span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => toast.info(t("kasa.printing"))}
                        className="px-4 py-2 bg-blue-50 text-blue-600 rounded-xl font-bold text-xs hover:bg-blue-600 hover:text-white transition-all flex items-center gap-2"
                      >
                        <Printer className="w-4 h-4" />
                        {t("kasa.printBill")}
                      </button>
                    </div>

                    {/* Split Modes */}
                    <div className="bg-white dark:bg-[#1a1d27] dark:text-gray-100 dark:border-[#2a2e3a] rounded-2xl p-1.5 shadow-sm border border-slate-100 flex shrink-0">
                      {["none", "equal", "items"].map((mode) => (
                        <button
                          key={mode}
                          onClick={() => {
                            setSplitMode(mode);
                            setSelectedItems([]);
                          }}
                          className={`flex-1 py-3 px-4 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${splitMode === mode ? "bg-slate-900 text-white shadow-md" : "text-slate-500 hover:bg-slate-50"}`}
                        >
                          {mode === "none" && (
                            <CreditCard className="w-4 h-4 text-orange-400" />
                          )}
                          {mode === "equal" && (
                            <Users className="w-4 h-4 text-blue-400" />
                          )}
                          {mode === "items" && (
                            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                          )}
                          {mode === "none" && t("kasa.split.none")}
                          {mode === "equal" && t("kasa.split.equal")}
                          {mode === "items" && t("kasa.split.items")}
                        </button>
                      ))}
                    </div>

                    {/* Items List */}
                    <div className="bg-white dark:bg-[#1a1d27] dark:text-gray-100 dark:border-[#2a2e3a] rounded-3xl border border-slate-100 shadow-sm flex-1 flex flex-col overflow-hidden relative">
                      <div className="p-5 border-b border-slate-50 flex justify-between items-center bg-slate-50 dark:bg-[#12141c] dark:border-[#2a2e3a]/30 ">
                        <h2 className="text-sm font-bold text-slate-800 uppercase tracking-widest">
                          {t("kasa.orderDetails")}
                        </h2>
                        <span className="text-[10px] font-black bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">
                          {t("kasa.itemsCount", { n: orderDetails?.items.length })}
                        </span>
                      </div>
                      <div className="flex-1 overflow-y-auto p-4 space-y-3">
                        {loading ? (
                          <div className="h-full flex items-center justify-center">
                            <PosLoader size="md" />
                          </div>
                        ) : (
                          orderDetails?.items.map((item) => (
                            <motion.div
                              key={item.id}
                              layout
                              onClick={() => toggleItemSelection(item.id)}
                              className={`relative flex items-center justify-between p-4 rounded-2xl border transition-all cursor-pointer group ${splitMode === "items" ? (selectedItems.includes(item.id) ? "bg-blue-50 border-blue-500 shadow-sm" : "bg-white dark:bg-slate-800 dark:border-slate-700 opacity-60") : "bg-white dark:bg-[#1a1d27] border-slate-100 dark:border-[#2a2e3a] hover:border-blue-200 dark:hover:border-blue-500"}`}
                            >
                              <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-lg bg-slate-50 dark:bg-[#12141c] dark:border-[#2a2e3a] flex items-center justify-center text-slate-900 font-bold text-xs ring-1 ring-slate-100">
                                  {item.quantity}x
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <p className="font-bold text-slate-800">
                                      {item.name}
                                    </p>
                                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                                  </div>
                                  <div className="flex items-center gap-2 mt-0.5">
                                    <p className="text-xs text-slate-400">
                                      ₼ {item.price} {t("kasa.perUnit")}
                                    </p>
                                    {item.notes?.map((n, idx) => (
                                      <span
                                        key={idx}
                                        className="text-[9px] font-bold px-1.5 py-0.5 bg-amber-50 text-amber-600 rounded uppercase"
                                      >
                                        {n}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-lg font-black text-blue-600">
                                  ₼ {(item.quantity * item.price).toFixed(2)}
                                </p>
                                {splitMode === "equal" && (
                                  <p className="text-[10px] font-bold text-slate-400 uppercase mt-1">
                                    {t("kasa.perPerson")}{" "}
                                    {(
                                      (item.quantity * item.price) /
                                      (orderDetails?.guests || 1)
                                    ).toFixed(2)}
                                  </p>
                                )}
                              </div>
                              {splitMode === "items" &&
                                selectedItems.includes(item.id) && (
                                  <div className="absolute top-0 right-0 p-1 bg-blue-600 rounded-bl-xl text-white shadow-lg shadow-blue-200">
                                    <CheckCircle2 className="w-3.5 h-3.5" />
                                  </div>
                                )}
                            </motion.div>
                          ))
                        )}

                        {/* Upsell Mockup */}
                        <div className="mt-8 p-6 bg-gradient-to-r from-blue-600/5 to-indigo-600/5 rounded-3xl border border-blue-100/50 flex items-center gap-4 group">
                          <div className="p-4 bg-white dark:bg-[#1a1d27] dark:text-gray-100 dark:border-[#2a2e3a] rounded-2xl shadow-sm text-blue-500 group-hover:scale-110 transition-all group-hover:rotate-6">
                            <Sparkles className="w-6 h-6" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-bold text-blue-900 text-sm">
                              {t("kasa.upsellTitle")}
                            </h3>
                            <p className="text-xs text-blue-700/70 mt-1">
                              {t("kasa.upsellText")}
                            </p>
                          </div>
                          <button className="px-4 py-2 bg-blue-600 text-white text-xs font-bold rounded-xl shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all active:scale-95">
                            {t("kasa.add")}
                          </button>
                        </div>
                      </div>

                      {/* Discount Bar */}
                      <div className="p-4 border-t border-slate-100 bg-slate-50 dark:bg-[#12141c] dark:border-[#2a2e3a]/50 flex items-center gap-3 overflow-x-auto no-scrollbar">
                        <Percent className="w-5 h-5 text-slate-400 shrink-0" />
                        {[0, 5, 10, 15, 20].map((d) => (
                          <button
                            key={d}
                            onClick={() => setDiscount(d)}
                            className={`px-5 py-2.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${discount === d ? "bg-slate-900 text-white shadow-lg" : "bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-300 border border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500"}`}
                          >
                            {t("kasa.discountPct", { d })}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Right Side: Payment Methods & Summary */}
                  <div className="w-full lg:w-[420px] flex flex-col gap-6">
                    {/* Summary Card */}
                    <div className="bg-white dark:bg-[#1a1d27] dark:text-gray-100 dark:border-[#2a2e3a] rounded-3xl p-8 border border-slate-100 shadow-xl shadow-slate-200/50 dark:shadow-none relative overflow-hidden">
                      <div className="absolute -top-10 -right-10 w-40 h-40 bg-blue-50 rounded-full opacity-50"></div>
                      <h2 className="text-xl font-bold text-slate-800 mb-8 relative z-10">
                        {t("kasa.paySummary")}
                      </h2>
                      <div className="space-y-5 relative z-10">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-bold text-slate-400 uppercase tracking-widest">
                            {t("kasa.subtotal")}
                          </span>
                          <span className="text-lg font-bold text-slate-800 underline decoration-slate-200 decoration-4">
                            ₼ {subtotal.toFixed(2)}
                          </span>
                        </div>
                        {discount > 0 && (
                          <div className="flex justify-between items-center text-rose-500 bg-rose-50 p-3 rounded-2xl border border-rose-100/50">
                            <span className="font-bold text-xs uppercase tracking-wider">
                              {t("kasa.discountLabel", { d: discount })}
                            </span>
                            <span className="font-black">
                              -₼ {discountAmount.toFixed(2)}
                            </span>
                          </div>
                        )}
                        {orderDetails?.prepayment > 0 && (
                          <div className="flex justify-between items-center text-emerald-600 bg-emerald-50 p-3 rounded-2xl border border-emerald-100/50">
                            <span className="font-bold text-xs uppercase tracking-wider">
                              {t("kasa.prepayment")}
                            </span>
                            <span className="font-black">
                              -₼ {orderDetails.prepayment.toFixed(2)}
                            </span>
                          </div>
                        )}
                        <div className="h-px bg-slate-100 dark:bg-[#141824] dark:border-[#2a2e3a] bg-dashed my-2"></div>
                        <div className="flex flex-col items-end gap-1">
                          <span className="text-[10px] font-black text-slate-300 uppercase tracking-[0.2em]">
                            {t("kasa.amountDue")}
                          </span>
                          <div className="flex items-baseline gap-1">
                            <span className="text-4xl font-black text-blue-600">
                              ₼ {amountToPay.toFixed(2)}
                            </span>
                          </div>
                          {splitMode === "equal" && (
                            <p className="text-[10px] font-bold text-blue-500 bg-blue-50 px-2 py-0.5 rounded mt-1">
                              {t("kasa.perPersonShort", { amount: amountToPay.toFixed(2) })}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Payment Methods */}
                    <div className="bg-white dark:bg-[#1a1d27] dark:text-gray-100 dark:border-[#2a2e3a] rounded-3xl p-8 border border-slate-100 shadow-sm flex-1 flex flex-col">
                      <h2 className="text-sm font-bold text-slate-800 uppercase tracking-widest mb-6">
                        {t("kasa.payMethod")}
                      </h2>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
                        {paymentMethods.map((m) => {
                          const Icon = m.icon;
                          return (
                            <button
                              key={m.id}
                              onClick={() => {
                                setSelectedPayment(m.id);
                                if (m.id !== "customer_balance")
                                  setSelectedCustomerId(null);
                              }}
                              className={`relative flex flex-col items-center justify-center gap-2 py-4 px-2 text-center rounded-2xl border-2 transition-all group ${selectedPayment === m.id ? "bg-slate-900 border-slate-900 text-white shadow-xl dark:shadow-none scale-[1.02] z-10" : "bg-slate-50 dark:bg-slate-800 border-slate-50 dark:border-slate-700 text-slate-400 hover:bg-white dark:hover:bg-slate-700 hover:border-slate-200 dark:hover:border-slate-500"}`}
                            >
                              <Icon
                                className={`w-6 h-6 ${selectedPayment === m.id ? "text-white" : "text-slate-400 group-hover:text-blue-500"}`}
                              />
                              <span className="font-bold text-[10px] uppercase tracking-wider leading-tight">
                                {t(m.labelKey)}
                              </span>
                              {selectedPayment === m.id && (
                                <div className="absolute top-2 right-2 bg-white dark:bg-[#1a1d27] dark:text-gray-100 dark:border-[#2a2e3a] text-slate-900 rounded-full p-0.5">
                                  <CheckCircle2 className="w-3 h-3" />
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>

                      {/* Cash Input */}
                      {selectedPayment === "cash" && (
                        <div className="space-y-4 mb-8 transition-all duration-300">
                          <div className="relative group">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 block">
                              {t("kasa.cashReceived")}
                            </label>
                            <input
                              type="number"
                              value={cashGiven}
                              onChange={(e) => setCashGiven(e.target.value)}
                              placeholder="0.00"
                              className="w-full px-6 py-4 bg-slate-50 dark:bg-[#12141c] dark:border-[#2a2e3a] border-none rounded-2xl text-2xl font-black text-slate-800 focus:ring-4 focus:ring-blue-500/10 transition-all outline-none"
                            />
                            {change > 0 && (
                              <div className="mt-2 flex items-center justify-between p-3 bg-emerald-50 rounded-xl ring-1 ring-emerald-100 ring-inset">
                                <span className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">
                                  {t("kasa.change")}
                                </span>
                                <span className="text-lg font-black text-emerald-600">
                                  ₼ {change.toFixed(2)}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            {bankNotes.map((n) => (
                              <button
                                key={n}
                                onClick={() => handleQuickChange(n)}
                                className="py-2.5 bg-white dark:bg-[#1a1d27] dark:text-gray-100 dark:border-[#2a2e3a] border border-slate-200 rounded-xl font-bold text-xs text-slate-600 hover:border-emerald-500 hover:text-emerald-600 transition-all active:scale-95"
                              >
                                + ₼{n}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Veresiye / Customer Selector */}
                      {selectedPayment === "customer_balance" && (
                        <div className="space-y-4 mb-8 transition-all duration-300">
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 block">
                            {t("kasa.selectCustomer")}
                          </label>
                          <select
                            value={selectedCustomerId || ""}
                            onChange={(e) =>
                              setSelectedCustomerId(e.target.value)
                            }
                            className="w-full px-4 py-3.5 bg-slate-50 dark:bg-[#12141c] dark:border-[#2a2e3a] border border-slate-100 rounded-2xl text-sm font-bold text-slate-700 focus:ring-4 focus:ring-blue-500/10 outline-none"
                          >
                            <option value="">{t("kasa.selectName")}</option>
                            {customers.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      <button
                        disabled={!selectedPayment || loading}
                        onClick={handlePayment}
                        className={`w-full py-5 rounded-2xl font-black text-lg flex items-center justify-center gap-3 transition-all relative overflow-hidden group ${selectedPayment ? "bg-blue-600 text-white shadow-xl shadow-blue-200 hover:bg-blue-700 hover:scale-[1.02] active:scale-[0.98]" : "bg-slate-100 text-slate-400 opacity-50 cursor-not-allowed"}`}
                      >
                        {loading ? (
                          <div
                            key="loading"
                            className="w-6 h-6 border-4 border-white/30 border-t-white rounded-full animate-spin"
                          />
                        ) : (
                          <div key="ready" className="flex items-center gap-3">
                            <ThumbsUp className="w-6 h-6 group-hover:rotate-12 transition-transform" />
                            <span>{t("kasa.completePayment")}</span>
                          </div>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="reports-tab"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              className="h-full flex flex-col pb-6"
            >
              {/* Reports Filter Bar */}
              <div className="bg-white dark:bg-[#1a1d27] dark:text-gray-100 dark:border-[#2a2e3a] p-6 rounded-3xl border border-slate-100 shadow-sm mb-6 flex flex-wrap items-end gap-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-slate-50 dark:bg-[#12141c] dark:border-[#2a2e3a] rounded-bl-full -z-0 opacity-50"></div>
                <div className="space-y-2 relative z-10 min-w-[200px]">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    {t("kasa.startDate")}
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 rounded-xl text-sm font-bold outline-none transition-all border focus:border-[var(--primary)]/50 focus:ring-4 focus:ring-orange-500/10 cursor-pointer bg-gray-50 dark:bg-[#22252f] border-gray-200 dark:border-[#2a2e3a] text-gray-800 dark:text-gray-200 dark:[color-scheme:dark]"
                    />
                  </div>
                </div>
                <div className="space-y-2 relative z-10 min-w-[200px]">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    {t("kasa.endDate")}
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 rounded-xl text-sm font-bold outline-none transition-all border focus:border-[var(--primary)]/50 focus:ring-4 focus:ring-orange-500/10 cursor-pointer bg-gray-50 dark:bg-[#22252f] border-gray-200 dark:border-[#2a2e3a] text-gray-800 dark:text-gray-200 dark:[color-scheme:dark]"
                    />
                  </div>
                </div>
                <div className="space-y-2 relative z-10 min-w-[200px]">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    {t("kasa.payType")}
                  </label>
                  <div className="relative">
                    <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                    <select
                      value={paymentTypeFilter}
                      onChange={(e) => setPaymentTypeFilter(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 rounded-xl text-sm font-bold outline-none transition-all border focus:border-[var(--primary)]/50 focus:ring-4 focus:ring-orange-500/10 cursor-pointer bg-gray-50 dark:bg-[#22252f] border-gray-200 dark:border-[#2a2e3a] text-gray-800 dark:text-gray-200 dark:[color-scheme:dark]"
                    >
                      <option value="all">{t("kasa.filterAll")}</option>
                      <option value="cash">{t("kasa.method.cash")}</option>
                      <option value="bank">{t("kasa.creditCard")}</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-2 relative z-10">
                  <button
                    onClick={() => fetchReports()}
                    className="px-6 py-3 bg-gradient-to-r from-[var(--primary)] to-amber-500 text-white rounded-xl font-bold text-sm shadow-lg shadow-orange-200/50 hover:opacity-90 transition-all"
                  >
                    {t("kasa.filter")}
                  </button>
                  <button
                    onClick={exportToExcel}
                    className="p-2.5 bg-emerald-50 text-emerald-600 rounded-xl font-bold border border-emerald-100 hover:bg-emerald-600 hover:text-white transition-all"
                    title={t("kasa.exportExcel")}
                  >
                    <FileText className="w-5 h-5" />
                  </button>
                  <button
                    onClick={handleEndOfDay}
                    className="px-4 py-2.5 bg-slate-800 text-white rounded-xl font-bold text-sm shadow-lg shadow-slate-200 hover:bg-slate-900 transition-all flex items-center gap-2"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    {t("kasa.endOfDay")}
                  </button>
                </div>
              </div>

              {/* Report Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="bg-white dark:bg-[#1a1d27] dark:text-gray-100 dark:border-[#2a2e3a] p-6 rounded-3xl border border-slate-100 shadow-sm flex items-center justify-between">
                  <div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1">
                      {t("kasa.totalKasa")}
                    </p>
                    <p className="text-3xl font-black text-slate-800">
                      ₼ {Number(reportTotals.totalKasa || 0).toFixed(2)}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center">
                    <Calculator className="w-6 h-6" />
                  </div>
                </div>
                <div className="bg-white dark:bg-[#1a1d27] dark:text-gray-100 dark:border-[#2a2e3a] p-6 rounded-3xl border border-slate-100 shadow-sm flex items-center justify-between">
                  <div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1">
                      {t("kasa.totalCash")}
                    </p>
                    <p className="text-3xl font-black text-emerald-600">
                      ₼ {Number(reportTotals.totalCash || 0).toFixed(2)}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center">
                    <Banknote className="w-6 h-6" />
                  </div>
                </div>
                <div className="bg-white dark:bg-[#1a1d27] dark:text-gray-100 dark:border-[#2a2e3a] p-6 rounded-3xl border border-slate-100 shadow-sm flex items-center justify-between">
                  <div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1">
                      {t("kasa.totalCard")}
                    </p>
                    <p className="text-3xl font-black text-blue-500">
                      ₼ {Number(reportTotals.totalBank || 0).toFixed(2)}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-blue-50 text-blue-500 rounded-2xl flex items-center justify-center">
                    <CreditCard className="w-6 h-6" />
                  </div>
                </div>
              </div>

              {/* report table */}
              <div className="flex-1 overflow-hidden flex flex-col bg-white dark:bg-[#1a1d27] dark:text-gray-100 dark:border-[#2a2e3a] rounded-3xl border border-slate-100 shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead className="bg-slate-50 dark:bg-[#12141c] dark:border-[#2a2e3a] border-b border-slate-100">
                      <tr>
                        <th className="p-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                          {t("kasa.col.tablePerson")}
                        </th>
                        <th className="p-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                          {t("kasa.col.hours")}
                        </th>
                        <th className="p-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">
                          {t("kasa.col.duration")}
                        </th>
                        <th className="p-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">
                          {t("kasa.col.total")}
                        </th>
                        <th className="p-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                          {t("kasa.col.payment")}
                        </th>
                        <th className="p-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                          {t("kasa.col.staff")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {filteredReportData.map((d, i) => (
                        <tr
                          key={i}
                          className="hover:bg-slate-50 dark:bg-[#12141c] dark:border-[#2a2e3a]/50 transition-colors"
                        >
                          <td className="p-5">
                            <p className="font-bold text-slate-800">
                              {d.order_name}
                            </p>
                            <span className="text-[10px] font-bold text-slate-400">
                              {d.user_name}
                            </span>
                          </td>
                          <td className="p-5">
                            <div className="flex items-center gap-2 text-xs font-bold text-slate-500">
                              <span className="text-emerald-500">
                                {new Date(d.open_date).toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </span>
                              <ArrowRight className="w-3 h-3 text-slate-300" />
                              <span className="text-rose-500">
                                {new Date(d.close_date).toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </span>
                            </div>
                          </td>
                          <td className="p-5 text-center">
                            <span className="bg-slate-100 dark:bg-[#141824] dark:border-[#2a2e3a] px-2 py-1 rounded text-[10px] font-black text-slate-500 uppercase">
                              {d.duration}
                            </span>
                          </td>
                          <td className="p-5 text-right font-black text-slate-800 text-lg">
                            ₼ {d.total_amount}
                          </td>
                          <td className="p-5">
                            <span
                              className={`px-2 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${d.type === "cash" ? "bg-emerald-100 text-emerald-600" : "bg-blue-100 text-blue-600"}`}
                            >
                              {d.type === "cash" ? t("kasa.method.cash") : t("kasa.bank")}
                            </span>
                          </td>
                          <td className="p-5">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-[#141824] dark:border-[#2a2e3a] flex items-center justify-center font-bold text-xs text-slate-500">
                                {d.user_name.charAt(0)}
                              </div>
                              <span className="text-xs font-bold text-slate-600">
                                {d.user_name}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {confirmDialog}
    </div>
  );
}
